/**
 * components/MapprModal.tsx - Mappr Integration Modal
 * 
 * CHANGE HISTORY:
 * - 251109_0816 [pending] danielbeer: Fixed imports to use Interfacr
 * - 251103_1522 [pending] danielbeer: Modal wrapper for Visual Mapper integration
 * 
 * Modal that opens Visual Mapper for field mapping between source and target schemas.
 * Mappr is part of Interfacr package.
 */

import React from 'react';
import { VisualMapper } from '@opcentral/dependr';
import type { MapprField as Field, MapprMapping as Mapping } from '@opcentral/dependr';

export interface MapprModalProps {
  /** Source schema fields */
  sourceSchema?: any[];
  
  /** Target schema fields */
  targetSchema?: any[];
  
  /** Apply mappings handler */
  onApply: (mappings: Mapping[]) => void;
  
  /** Close modal handler */
  onClose: () => void;
  
  /** Theme */
  theme?: 'light' | 'dark';
}

/**
 * MapprModal - Modal wrapper for Visual Mapper
 */
export const MapprModal: React.FC<MapprModalProps> = ({
  sourceSchema = [],
  targetSchema = [],
  onApply,
  onClose,
  theme = 'light'
}) => {
  // Convert schemas to Field format
  const sourceFields: Field[] = sourceSchema.map(field => ({
    id: field.id || field.name,
    name: field.name,
    type: field.type || 'string',
    sample: field.sample,
    required: false
  }));

  const targetFields: Field[] = targetSchema.map(field => ({
    id: field.id || field.name,
    name: field.name,
    type: field.type || 'string',
    sample: field.sample,
    required: false
  }));

  // Handle mapping changes
  const handleMappingChange = (mappings: Mapping[]) => {
    // Store mappings but don't apply yet
  };

  // Handle apply button
  const handleApply = () => {
    // Get current mappings from Visual Mapper
    // For now, just close the modal
    onClose();
  };

  return (
    <div className="mappr-modal-overlay" onClick={onClose}>
      <div 
        className={`mappr-modal mappr-modal--${theme}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal header */}
        <div className="mappr-modal__header">
          <h2>🗺️ Field Mapper</h2>
          <p>Map fields between source and target schemas</p>
          <button
            className="mappr-modal__close"
            onClick={onClose}
            title="Close"
          >
            ✕
          </button>
        </div>
        
        {/* Visual Mapper */}
        <div className="mappr-modal__content">
          {sourceFields.length > 0 && targetFields.length > 0 ? (
            <VisualMapper
              sourceFields={sourceFields}
              targetFields={targetFields}
              onMappingChange={handleMappingChange}
              autoMatch={true}
              showConfidence={true}
              theme={theme}
              layout="horizontal"
            />
          ) : (
            <div className="mappr-modal__empty">
              <p>⚠️ Unable to extract schemas from source and target data.</p>
              <p>Make sure both panels contain valid JSON data.</p>
            </div>
          )}
        </div>
        
        {/* Modal footer */}
        <div className="mappr-modal__footer">
          <button
            className="mappr-modal__btn"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="mappr-modal__btn mappr-modal__btn--primary"
            onClick={handleApply}
            disabled={sourceFields.length === 0 || targetFields.length === 0}
          >
            Apply Mappings
          </button>
        </div>
      </div>
    </div>
  );
};
